
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';
import { debounce } from 'lodash';
import { ValidationProvider } from 'vee-validate';

import { sharedState } from '../../framework/state';
import { getResourcesNew } from '../../framework/client/resource';
import { getDatabaseOptions, getEnumerationOptions, OptionValue } from '../../framework/service/options';
import { getFieldNameFromVModelProperty } from '../../framework/util/component_util';

import { ApplicationResource } from '../../../common/application/enumeration/ApplicationResource';
import { CustomerVehicleState, VehicleState } from '../../../common/application/model/enums/VehicleState';
import { sortAlphabetically } from '../service/helper_service';
import { P_PAGE_SIZE } from '../../../common/framework/constants';

/**
 * Order-independent representation of a parameter Map, for comparing one render's filter to the
 * previous one. Values can be arrays (customerId, vehicleState), hence JSON rather than String().
 */
function parametersKey(parameters: Map<string, any> | undefined): string {
    if (!parameters || parameters.size === 0) {
        return '';
    }

    return Array.from(parameters.keys())
        .sort()
        .map((key) => `${key}=${JSON.stringify(parameters.get(key) ?? null)}`)
        .join('&');
}

@Component({
    components: {
        Multiselect,
        ValidationProvider,
    },
})
export default class AutoCompleteMultipleField extends Vue {
    $refs!: {
        multiselect: Multiselect;
    };

    @Prop(Boolean) readonly disabled!: string;
    @Prop(Boolean) readonly expanded!: boolean;
    @Prop(String) readonly searchField!: string;
    @Prop(String) readonly idField!: string;
    @Prop(Boolean) readonly multiple!: boolean;
    @Prop(String) readonly name!: string;
    @Prop(String) readonly resource!: ApplicationResource | 'invoice-row-type' | 'vehicle-state';
    @Prop(Boolean) readonly required!: boolean;
    @Prop(Array) readonly value!: string[];
    @Prop(Map) readonly parameters!: Map<string, string>;
    @Prop(Boolean) readonly taggable!: boolean;
    @Prop(Boolean) readonly isAdmin?: boolean;

    shared = sharedState;

    local = {
        filteredObjects: [] as OptionValue[],
        selectedResources: [] as OptionValue[],
        pageSize: 2000,
        isLoading: false,
        isTouched: false,
    };

    /**
     * Contents of the `parameters` prop as of the last fetch that landed, or undefined when no
     * fetch has landed yet. See queryValueChange.
     */
    private lastFetchedParametersKey: string | undefined;

    /** Incremented per fetch, so a slow earlier response cannot overwrite a later one. */
    private latestFindId = 0;

    async mounted() {
        await this.asyncFind('');
        await this.modelValueChange(this.value);
    }

    @Watch('value')
    async modelValueChange(newValue: string[]) {
        this.local.selectedResources = this.local.selectedResources ?? [];
        this.local.filteredObjects = this.local.filteredObjects ?? [];

        if (!newValue) {
            this.local.selectedResources = [];
            return;
        }

        newValue.forEach((resourceId) => {
            const newResource = this.local.filteredObjects.find(({ id }) => resourceId === id);
            const oldResource = this.local.selectedResources.find(({ id }) => resourceId === id);

            if (newResource && !oldResource) {
                this.local.selectedResources.push(newResource);
            }
        });
    }

    /**
     * Refetches when the caller's filter actually changed - not merely when it was rebuilt.
     *
     * Every caller passes this prop as a method call (`:parameters="someParameters()"`), so a new
     * Map is constructed on each render of the parent. This watcher is not deep, so it compares by
     * reference and used to fire on every one of those renders, each time issuing a pageSize=2000
     * request. On VehicleContracts that meant six to seven full vendor / vehicle-contract loads per
     * page load instead of one. Comparing the contents makes the rebuilt-but-identical case free.
     */
    @Watch('parameters')
    async queryValueChange() {
        if (parametersKey(this.parameters) === this.lastFetchedParametersKey) {
            return;
        }

        await this.asyncFind('');
    }

    dropdownClosed(opened: boolean) {
        this.local.isTouched = true;

        if (opened) {
            return;
        }

        let value: string[];

        if (this.multiple) {
            value = this.local.selectedResources.map(({ id }) => id!!);
        } else {
            const selectedResource = this.local.selectedResources as any;
            value = selectedResource && selectedResource.id ? [selectedResource.id] : [];
        }

        this.$emit('dropdown-closed', value, this.idField);
    }

    deBouncedSearch = debounce((searchString: string) => {
        this.asyncFind(searchString);
    }, 500);

    clearAll(): void {
        this.local.selectedResources = [];

        if (!this.multiple) {
            (this.$refs.multiselect as any).deactivate();
        }
    }

    selectAll(): void {
        this.local.selectedResources = [...this.local.filteredObjects];
    }

    getFieldName(): string {
        return getFieldNameFromVModelProperty(this);
    }

    addTag(tagName: any) {
        this.$emit('tag', tagName);
    }

    isInvalid() {
        return this.required && this.local.isTouched && this.local.selectedResources.length === 0;
    }

    private async asyncFind(query: string): Promise<void> {
        const findId = ++this.latestFindId;
        const parametersKeyOfFind = parametersKey(this.parameters);

        this.local.isLoading = true;
        const parameters = new Map<string, any>();
        parameters.set(P_PAGE_SIZE, this.local.pageSize);

        if (query && query.length) {
            parameters.set(this.searchField, `ILIKE_%${query}%`);
        }

        if (this.parameters) {
            for (const key of this.parameters.keys()) {
                parameters.set(key, this.parameters.get(key));
            }
        }

        if (!sharedState.admin) {
            if (!sharedState.context.scopes.manager.customerId) {
                this.applyFindResult(findId, parametersKeyOfFind, []);
                return;
            }

            if (this.resource !== ApplicationResource.VENDOR) {
                const key = this.resource === ApplicationResource.CUSTOMER ? 'id' : 'customerId';
                parameters.set(key, sharedState.context.scopes.manager.customerId);
            }
        }

        try {
            let objects: OptionValue[];

            if (this.resource === 'vehicle-state') {
                objects = getEnumerationOptions(
                    'VehicleState',
                    Object.keys(this.isAdmin ? VehicleState : CustomerVehicleState),
                    this,
                );
                objects.shift();
            } else if (this.resource === 'invoice-row-type') {
                objects = (await getDatabaseOptions('InvoiceRowType', this)).filter((object) =>
                    object.label.toLowerCase().includes(query.toLowerCase()),
                );
            } else {
                objects = await this.getOptions(this.resource, this.searchField, parameters);
            }

            this.applyFindResult(findId, parametersKeyOfFind, objects);
        } catch (error) {
            console.error(error);

            if (findId === this.latestFindId) {
                // Do not remember a filter whose list never loaded - otherwise queryValueChange
                // treats the empty list as current and the dropdown stays empty until a reload.
                this.lastFetchedParametersKey = undefined;
                this.local.isLoading = false;
            }
        }
    }

    /**
     * Publishes a fetch's result unless a newer fetch has already been started.
     *
     * The list is loaded both from the debounced search box and from a change of the `parameters`
     * prop, so two requests are easily in flight at once. Without this, whichever answered last
     * won: a filter reload landing after a search reply replaced the matches with the unfiltered
     * list, and a search reply landing after it appeared to find nothing.
     */
    private applyFindResult(findId: number, parametersKeyOfFind: string, objects: OptionValue[]): void {
        if (findId !== this.latestFindId) {
            return;
        }

        this.local.filteredObjects = objects;
        this.lastFetchedParametersKey = parametersKeyOfFind;
        this.local.isLoading = false;
    }

    private async getOptions(resource: string, field: string, parameters: Map<string, any>) {
        return (await getResourcesNew<any>(resource, -1, parameters))
            .map((o) => {
                return { id: o.id, label: o[field] };
            })
            .sort(sortAlphabetically('label'));
    }
}
